import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ModeBadge from "@/components/ModeBadge";
import { 
  Users, 
  ClipboardCheck, 
  BarChart3, 
  ArrowRight, 
  AlertCircle,
  Plus,
  FileAudio,
  TrendingUp,
  Calendar,
  Loader2
} from "lucide-react";
import { motion } from "framer-motion";
import { useAssessments, useCurrentUser, useCohorts } from "@/lib/api";

export default function TeacherDashboard() {
  const { data: user } = useCurrentUser();
  const { data: assessments, isLoading: loadingAssessments } = useAssessments();
  const { data: cohorts, isLoading: loadingCohorts } = useCohorts();

  // Compute stats from assessments
  const stats = useMemo(() => {
    if (!assessments || !Array.isArray(assessments)) {
      return {
        activePupils: 0,
        pendingReviews: 0,
        tasksThisWeek: 0,
        completedThisWeek: 0,
      };
    }

    // Pending reviews (draft_ready or under_review - awaiting teacher sign-off)
    const pendingCount = assessments.filter(a => a.status === 'draft_ready' || a.status === 'under_review').length;
    
    // Tasks from this week
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const thisWeekTasks = assessments.filter(a => {
      const created = new Date(a.created_at || 0);
      return created >= oneWeekAgo;
    });
    
    // Completed this week (signed off)
    const completedThisWeek = thisWeekTasks.filter(a => a.status === 'signed_off').length;

    // Get unique students count
    const uniqueStudents = new Set(assessments.map(a => a.student_name || "")).size;

    return {
      activePupils: uniqueStudents,
      pendingReviews: pendingCount,
      tasksThisWeek: thisWeekTasks.length,
      completedThisWeek,
    };
  }, [assessments]);

  // Get recent assessments for the activity feed
  const recentAssessments = useMemo(() => {
    if (!assessments) return [];
    return [...assessments]
      .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
      .slice(0, 5);
  }, [assessments]);

  // Get pending reviews for quick access
  const pendingReviews = useMemo(() => {
    if (!assessments) return [];
    return assessments
      .filter(a => a.status === 'draft_ready' || a.status === 'under_review')
      .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
      .slice(0, 4);
  }, [assessments]);

  const getModeBadgeMode = (mode: string): "presentational" | "exploratory" | "collaborative" | "dialogic" | "persuasive" | "reflective" => {
    switch (mode) {
      case 'presenting':
        return 'presentational';
      case 'explaining':
        return 'exploratory';
      case 'persuading':
        return 'persuasive';
      default:
        return 'presentational';
    }
  };

  const isLoading = loadingAssessments || loadingCohorts;

  const statCards = [
    { 
      label: "Active Students", 
      value: stats.activePupils.toString(), 
      icon: <Users className="w-5 h-5" />,
      description: "With submissions"
    },
    { 
      label: "Pending Reviews", 
      value: stats.pendingReviews.toString(), 
      icon: <ClipboardCheck className="w-5 h-5" />,
      description: "Awaiting sign-off",
      highlight: stats.pendingReviews > 0
    },
    { 
      label: "Tasks This Week", 
      value: stats.tasksThisWeek.toString(), 
      icon: <FileAudio className="w-5 h-5" />,
      description: `${stats.completedThisWeek} completed`
    },
    { 
      label: "Cohorts", 
      value: (cohorts?.length || 0).toString(), 
      icon: <BarChart3 className="w-5 h-5" />,
      description: "Active cohorts"
    },
  ];

  return (
    <AppLayout role="teacher">
      <div className="space-y-8">
        {/* Welcome Header */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-1">
                {user ? `Welcome back, ${user.first_name}` : "Loading..."}
              </h1>
              <p className="text-muted-foreground">
                Here's what's happening with your cohorts
              </p>
            </div>
            <div className="flex gap-2">
              <Link to="/teacher/assessments/create">
                <Button className="gap-2">
                  <Plus className="w-4 h-4" />
                  Create Assessment
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Card className={stat.highlight ? 'border-warning/50 bg-warning/5' : ''}>
                <CardContent className="flex items-center gap-4 p-5">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    stat.highlight ? 'bg-warning/20 text-warning' : 'bg-secondary text-primary'
                  }`}>
                    {stat.icon}
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                    <p className="text-xs text-muted-foreground/70">{stat.description}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pending Reviews - Takes 2 columns */}
          <motion.div 
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-warning" />
                    Pending Reviews
                  </CardTitle>
                  <CardDescription>Assessments awaiting your sign-off</CardDescription>
                </div>
                <Link to="/teacher/review">
                  <Button variant="ghost" size="sm" className="gap-1">
                    View All <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : pendingReviews.length === 0 ? (
                  <div className="text-center py-8">
                    <ClipboardCheck className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">No pending reviews</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      All caught up! New assessments will appear here.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingReviews.map((review) => (
                      <div 
                        key={review.id} 
                        className="flex items-center gap-4 p-3 rounded-lg border hover:bg-secondary/50 transition-colors"
                      >
                        <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0">
                          <span className="text-sm font-medium text-primary-foreground">
                            {(review.student_name || "").split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground text-sm truncate">
                            {review.student_name || "Unknown"}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {review.cohort_name || "No cohort"}
                          </p>
                        </div>
                        <ModeBadge mode={getModeBadgeMode(review.mode)} />
                        <div className="text-right shrink-0">
                          <p className="text-xs text-muted-foreground">
                            {review.created_at ? new Date(review.created_at).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short'
                            }) : "N/A"}
                          </p>
                        </div>
                        <Link to={`/teacher/review/${review.id}`}>
                          <Button size="sm" variant="outline" className="gap-1">
                            Review <ArrowRight className="w-3.5 h-3.5" />
                          </Button>
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Quick Actions & Info */}
          <motion.div 
            className="space-y-6"
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link to="/teacher/assessments/create" className="block">
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <Plus className="w-4 h-4" />
                    Create New Assessment
                  </Button>
                </Link>
                <Link to="/teacher/students" className="block">
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <Users className="w-4 h-4" />
                    Manage Students
                  </Button>
                </Link>
                <Link to="/teacher/progress" className="block">
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <TrendingUp className="w-4 h-4" />
                    View Progress Reports
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Cohorts Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Your Cohorts</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingCohorts ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : !cohorts || cohorts.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No cohorts created yet
                  </p>
                ) : (
                  <div className="space-y-2">
                    {cohorts.slice(0, 4).map((cohort) => (
                      <div
                        key={cohort.id}
                        className="flex items-center justify-between p-2 rounded-lg bg-secondary/50"
                      >
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm font-medium">{cohort.name}</span>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {(cohort as { student_count?: number }).student_count ?? cohort.students_list?.length ?? 0} students
                        </Badge>
                      </div>
                    ))}
                    {cohorts.length > 4 && (
                      <Link to="/teacher/students" className="block pt-2">
                        <Button variant="ghost" size="sm" className="w-full text-xs">
                          View all {cohorts.length} cohorts
                        </Button>
                      </Link>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Recent Activity */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Activity</CardTitle>
              <CardDescription>Latest assessment submissions</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : recentAssessments.length === 0 ? (
                <div className="text-center py-8">
                  <FileAudio className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No activity yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentAssessments.map((assessment) => (
                    <div 
                      key={assessment.id}
                      className="flex items-center gap-4 p-3 rounded-lg border"
                    >
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
                        <span className="text-xs font-medium text-secondary-foreground">
                          {(assessment.student_name || "").split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {assessment.student_name || "Unknown"} - {assessment.prompt_name || assessment.mode || "N/A"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {assessment.created_at ? new Date(assessment.created_at).toLocaleString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : "N/A"}
                        </p>
                      </div>
                      <Badge variant={assessment.status === 'signed_off' ? 'default' : 'secondary'}>
                        {assessment.status === 'signed_off' ? 'Completed' : 
                         assessment.status === 'draft_ready' || assessment.status === 'under_review' ? 'Pending' : 
                         assessment.status || "Unknown"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </AppLayout>
  );
}
